<template>
  <div id="child-app"
       :class="{'no-scroll': blockScrolling}">
    <quick-message></quick-message>
    <!-- App Base -->
    <b-row class="h-100 app-row"
           no-gutters>
      <!-- Menu Section -->
      <b-col class="h-100 menu-col"
             cols="auto">
        <sidebar></sidebar>
      </b-col>
      <!-- Content Section (Header, Router Component, Loader) -->
      <b-col class="h-100 content-col"
             cols="12">
        <!-- Content - Header Section -->
        <b-row class="header-row"
               no-gutters>
          <b-col class="h-100 header-col"
                 align="center"
                 cols="12">
            <setup-header></setup-header>
          </b-col>
        </b-row>

        <!-- Content - Router Component Section -->
        <b-row class="router-view-row min-h-100"
               id="routerViewElement"
               no-gutters
               @click="closeMenu">
          <b-col class="router-view-col col--footer-offset"
                 cols="12">
            <broadcast-message-modal></broadcast-message-modal>
            <app-tooltip v-if="appCreated"></app-tooltip>
            <router-view class="router-view"
                         :class="{'mobile-scrolling': isMobileBrowser, 'without-bottom-padding': isApplicationFlow}"
                         role="main"
                         :aria-busy="$wait.any && !$wait.is(waitLoaderExclude)">
            </router-view>
          </b-col>
        </b-row>

        <b-row v-show="showProgressBar && wideViewport"
               class="desktopProgressBar">
          <b-col>
            <application-progress></application-progress>
          </b-col>
        </b-row>

        <!-- Content - Footer Section -->
        <setup-footer></setup-footer>

        <!-- Content - Loader Overlay -->
        <activity-indicator v-show="($wait.any && !$wait.is(waitLoaderExclude)) || forceWait"
                            @click="closeMenu">
        </activity-indicator>
      </b-col>
    </b-row>

    <base-modal :id="locationDisabledModalId"
                tabindex="-1"
                centered
                hide-footer>
      <location-disabled-modal/>
    </base-modal>
  </div>
</template>

<script>
// Components
import ActivityIndicator from './components/common/elements/ActivityIndicator.vue'
import ApplicationProgress from './components/application/elements/ApplicationProgress.vue'
import AppTooltip from './components/common/elements/AppTooltip.vue'
import BaseModal from './components/common/elements/BaseModal.vue'
import BroadcastMessageModal from './components/common/elements/BroadcastMessageModal.vue'
import LocationDisabledModal from './components/application/elements/LocationDisabledModal.vue'
import QuickMessage from './components/common/elements/QuickMessage.vue'
import SetupFooter from './components/common/elements/SetupFooter.vue'
import SetupHeader from './components/common/elements/SetupHeader.vue'
import Sidebar from './components/common/elements/Sidebar.vue'

// Constants
import { AnalyticsConstants, KeyConstants } from './constants'

// Libraries
import AWS from 'aws-sdk'
import { mapGetters, mapState } from 'vuex'

// Services
import { EventBus } from './services/eventBusService'

// Styling
import './assets/styles/base.scss'
import './assets/styles/c1l.scss'
import './assets/styles/ccc.scss'
import './assets/styles/ccf.scss'
import './assets/styles/chs.scss'
import './assets/styles/cic.scss'
import './assets/styles/esm.scss'
import './assets/styles/inl.scss'
import './assets/styles/tbs.scss'
import './assets/styles/tmx.scss'

// Utilities
import Analytics from './utility/analyticsUtil'
import isEmpty from './utility/isEmpty'
import Logger from './utility/loggerUtil'
import Router from './utility/routerUtil'
import Session from './utility/sessionUtil'
import SessionRecord from './utility/sessionRecordUtil'

export default {
  name: 'child-app',

  components: {
    ActivityIndicator,
    ApplicationProgress,
    AppTooltip,
    BaseModal,
    BroadcastMessageModal,
    LocationDisabledModal,
    QuickMessage,
    SetupFooter,
    SetupHeader,
    Sidebar
  },

  created () {
    this.appCreated = true
    EventBus.$on(KeyConstants.modals.locationDisabled, this.toggleLocationDisabledModal)
  },

  async mounted () {
    this.setPageBrand()

    AWS.config.update({
      region: process.env.VUE_APP_AwsRegion
    })

    // Prevents scrolling in background when modals are shown
    this.$root.$on('bv::modal::show', () => { this.$store.commit('display/preventScroll') })
    this.$root.$on('bv::modal::hide', () => { this.$store.commit('display/allowScroll') })

    this.saveDeviceKeyCookie()
    await this.setApplicationRefreshListener()
  },

  data () {
    return {
      waitLoaderExclude: KeyConstants.waitLoadExclude + '*',
      currentPageName: '',
      appCreated: false,
      locationDisabledModalId: 'locationDisabledModal'
    }
  },

  computed: {
    isApplicationFlow () {
      return Router.isRouteInFlow('Application', this.$route)
    },

    blockScrolling () {
      return !this.allowScroll || this.$wait.any || this.sidebarOpen
    },

    showProgressBar () {
      // This is only for the desktop version
      const scanIdMode = document.location.search.includes('scanIdDataUuid') || !isEmpty(this.$route.query.scanIdDataUuid)
      const pageHasSection = !isEmpty(this.$route.meta?.section) && this.$route.meta?.section !== 'NONE'

      return this.isApplicationFlow && !this.isMobileBrowser && !scanIdMode && pageHasSection
    },

    ...mapState('display', ['allowScroll', 'forceWait', 'isMobileBrowser', 'sidebarOpen', 'wideViewport']),
    ...mapGetters('display', ['isIOS'])
  },

  methods: {
    saveDeviceKeyCookie () {
      try {
        const deviceKey = Session.getCookie(KeyConstants.deviceKey)

        if (!isEmpty(deviceKey)) {
          window.localStorage.setItem(KeyConstants.deviceKey, deviceKey)

          Session.deleteCookie(KeyConstants.deviceKey)
        }
      } catch (e) {
        Logger.error('Failed to save device key cookie')
        Logger.debug(e)
      }
    },

    setPageBrand (to, from) {
      const bodyElement = document.querySelector('body')

      if (bodyElement.classList.length === 0) {
        bodyElement.classList.add(Router.getCurrentRouteBrandClass(this.$route))
      } else {
        const toBrand = Router.getCurrentRouteBrandClass(to)
        const fromBrand = Router.getCurrentRouteBrandClass(from)

        if (!bodyElement.classList.contains(toBrand)) {
          bodyElement.classList.add(toBrand)
        }

        if (toBrand !== fromBrand) {
          bodyElement.classList.remove(fromBrand)
        }
      }
    },

    closeMenu () {
      this.$store.commit('display/closeSidebar')
    },

    async setApplicationRefreshListener () {
      const refreshEventName = this.isIOS ? 'pagehide' : 'beforeunload'

      window.addEventListener(refreshEventName, (event) => { // The event only fires after user interaction on the page
        if (!Router.isRouteInFlow('Application')) {
          return
        }

        if (this.$store.state.applicationSession.skipRefreshConfirmation) {
          this.$store.commit('applicationSession/setSkipRefresh', { skipRefresh: false }) // Reset skip flag
          return
        }

        const pageToReloadForceRefresh = this.$store.state.applicationSession.forceRefreshPage
        const pageToReloadScanId = document.location.search.includes('scanIdDataUuid') || !isEmpty(this.$route.query.scanIdDataUuid) ? 'ScanId' : undefined

        sessionStorage.setItem('pageIsReloaded', pageToReloadScanId || pageToReloadForceRefresh || (this.$store.state.session.authenticated ? 'Login' : 'PersonalData'))
        this.$store.commit('applicationSession/setForceRefresh', { forceRefreshPage: '' })
        Logger.debug('Attempting reload confirmation')

        const e = event || window.event
        const confirmText = this.$t('refresh_confirmation')
        if (e) {
          e.returnValue = confirmText
        }

        return confirmText
      })

      // Check if page is reloaded, reset flag, log user out and send them to the required page
      if (sessionStorage.getItem('pageIsReloaded')) {
        const pageAfterRefresh = sessionStorage.getItem('pageIsReloaded')
        if (pageAfterRefresh === 'ScanId') {
          return
        }

        await this.$store.dispatch('session/signout')
        Analytics.logEvent(AnalyticsConstants.eventName.logout, { method: AnalyticsConstants.method.logout.sessionTimeout }, { logMarketingEvent: true })

        sessionStorage.removeItem('pageIsReloaded')
        this.$router.push({ name: pageAfterRefresh || 'PersonalData' })
      }

      // Set a reactive navigation bar
      this.$store.commit('display/setWideViewport')
      window.addEventListener('resize', () => {
        this.$store.commit('display/setWideViewport')
      })
    },

    toggleLocationDisabledModal () {
      this.$bvModal.show(this.locationDisabledModalId)
    }
  },

  watch: {
    blockScrolling (blockScroll) {
      try {
        const bodyElement = document.querySelector('body')
        blockScroll ? bodyElement.classList.add('no-scroll') : bodyElement.classList.remove('no-scroll')
      } catch (e) {
        Logger.error('Error blocking scroll')
        Logger.debug(e)
      }
    },

    '$route' (to, from) {
      this.setPageBrand(to, from)

      this.$store.commit('display/allowScroll')

      const routeWrap = document.querySelector('.router-view-row')

      if (!isEmpty(routeWrap)) {
        routeWrap.scrollTop = document.documentElement.scrollTop = 0
      }

      EventBus.$emit('ClearMessage')
      this.$store.commit('session/updatePreviousPage', { previousPage: from.name, previousProps: from.params })

      try {
        Analytics.setScreenName(to.name)
        SessionRecord.logPageView(to.name, !isEmpty(this.currentPageName))
      } catch (error) {
        Logger.error('Error logging screen view')
        Logger.debug(error)
      }

      this.currentPageName = to.name
    }
  }
}
</script>

<style lang="scss" scoped>
@use './assets/styles/colors';

.app-row {
  z-index: 0;
  flex-wrap: nowrap;
}

.menu-col {
  position: fixed;
  top: 0;
  left: 0;
  z-index: 1;
}

.content-col {
  z-index: 0;
}

.header-row,
.header-col {
  z-index: 3;
}

.header-row {
  box-shadow: 0 4px 4px 0 colors.$gray-900-15op;
}

.router-view-row,
.router-view-col {
  z-index: 1;
}

.col--footer-offset {
  min-height: calc(100vh - 4.625rem);
  overflow: clip;
}

.router-view {
  flex-flow: row wrap;
  min-height: 100%;
  padding-bottom: 44px;
}

.router-view.without-bottom-padding {
  padding-bottom: 0;
}

.router-view.mobile-scrolling {
  min-height: calc(100% + 1px);
}

.desktopProgressBar {
  position: fixed;
  top: 6.25rem;
  left: 5rem;
  z-index: 99;
  max-width: 100%;
}
</style>

<i18n>
  {
    "en": {
      "refresh_confirmation": "Are you sure you want to leave?"
    }
  }
</i18n>
